import { useEffect, useMemo, useState } from 'react'
import { costWeeksAPI, type CostWeeksResponse } from '../../../lib/api'
import { useFetchData } from '../../../hooks/useFetchData'

/**
 * CR-1421: one page of the Cost tab's weekly charts.
 *
 * Fetched with the SAME params the dashboard fetched its Cost data with, so
 * both charts honour every dashboard filter exactly as the rest of the tab.
 * The page goes back to this week whenever those params change.
 *
 * @param params - the dashboard's Cost request params (`data.costParams`); none = not loaded yet
 * @returns the page's data, loading/error state and the page setter
 * @example
 *   const { data, page, setPage } = useCostWeeks(dashboard.costParams)
 */
export function useCostWeeks(params: Record<string, string | number | boolean> | undefined) {
  const [page, setPage] = useState(0)
  const key = useMemo(() => JSON.stringify(params ?? null), [params])
  useEffect(() => { setPage(0) }, [key])
  const result = useFetchData<CostWeeksResponse | null>(
    async () => (params ? (await costWeeksAPI.getWeeks({ ...params, page })).data : null),
    { deps: [key, page] },
  )
  return { ...result, page, setPage }
}
